import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Filter, X, Search as SearchIcon, ArrowDownUp } from 'lucide-react'; // Ícones necessários
import { useAuth } from '../contexts/AuthContext';
import type { GameSearchFilters } from '../types'; // Para GameFilters
import SEO from '../components/SEO';
import FeedComponent from '../components/feed/FeedComponent';
import GameFilters from '../components/GameFilters';
import useDebounce from '../hooks/useDebounce'; // <<< IMPORTAR O HOOK

// Tipo para o filtro de tag ativo - Adicionado displayName
interface TagFilter {
  type: 'game' | 'mechanic' | 'category' | 'theme';
  value: string; // ID para game, nome para outros
  displayValue?: string; // Nome para exibir (útil para games)
}

// Tipo para opções de ordenação do feed
type FeedSortOption = 'created_at_desc' | 'created_at_asc' | 'like_count_desc';

const MIN_FILTER_LENGTH = 4; // Definir tamanho mínimo

const FeedPage: React.FC = () => {
  const { user } = useAuth();
  const [activeTagFilter, setActiveTagFilter] = useState<TagFilter | null>(null);
  const [showFilterUI, setShowFilterUI] = useState(false);
  const [gameFiltersForUI, setGameFiltersForUI] = useState<GameSearchFilters>({});
  // Estado para filtro de nome de jogo (valor imediato do input)
  const [gameNameFilter, setGameNameFilter] = useState('');
  // Valor debounced do filtro de nome de jogo
  const debouncedGameNameFilter = useDebounce(gameNameFilter, 500);
  // Estado para ordenação
  const [sortOption, setSortOption] = useState<FeedSortOption>('created_at_desc');
  
  // Log para verificar o valor debounced
  useEffect(() => {
    console.log('[FeedPage DEBUG] Debounced game name filter updated:', debouncedGameNameFilter);
  }, [debouncedGameNameFilter]);

  // Calcular o filtro efetivo baseado no debounce E no tamanho mínimo
  const effectiveGameNameFilter = 
    debouncedGameNameFilter.trim().length >= MIN_FILTER_LENGTH
    ? debouncedGameNameFilter.trim()
    : undefined;
    
  useEffect(() => {
    console.log('[FeedPage DEBUG] Effective game name filter updated:', effectiveGameNameFilter);
  }, [effectiveGameNameFilter]);

  // Handler chamado pelo GameFilters
  const handleGameFilterChange = useCallback((newFilters: GameSearchFilters) => {
    setGameFiltersForUI(newFilters);
    // Lógica para extrair ÚLTIMA tag de cat/mech/theme como filtro ativo
    let changedType: TagFilter['type'] | null = null;
    let changedValue: string | null = null;
    const prevCats = gameFiltersForUI.categories || [];
    const newCats = newFilters.categories || [];
    if (newCats.length > prevCats.length) { changedType = 'category'; changedValue = newCats[newCats.length - 1]; }
    else if (newCats.length < prevCats.length) { const removed = prevCats.find(c => !newCats.includes(c)); if (activeTagFilter?.type === 'category' && activeTagFilter?.value === removed) setActiveTagFilter(null); }
    const prevMechs = gameFiltersForUI.mechanics || [];
    const newMechs = newFilters.mechanics || [];
    if (newMechs.length > prevMechs.length) { changedType = 'mechanic'; changedValue = newMechs[newMechs.length - 1]; }
    else if (newMechs.length < prevMechs.length) { const removed = prevMechs.find(m => !newMechs.includes(m)); if (activeTagFilter?.type === 'mechanic' && activeTagFilter?.value === removed) setActiveTagFilter(null); }
    const prevThemes = gameFiltersForUI.themes || [];
    const newThemes = newFilters.themes || [];
    if (newThemes.length > prevThemes.length) { changedType = 'theme'; changedValue = newThemes[newThemes.length - 1]; }
    else if (newThemes.length < prevThemes.length) { const removed = prevThemes.find(t => !newThemes.includes(t)); if (activeTagFilter?.type === 'theme' && activeTagFilter?.value === removed) setActiveTagFilter(null); }
    
    if (changedType && changedValue) {
        setActiveTagFilter({ type: changedType, value: changedValue });
    }
  }, [activeTagFilter, gameFiltersForUI]);

  // Handler chamado pelo clique na tag em PostItem - Assinatura e lógica atualizadas
  const handleTagFilterDirect = useCallback((type: TagFilter['type'], value: string, displayName?: string) => {
    console.log(`[FeedPage] handleTagFilterDirect chamado - Type: ${type}, Value: ${value}, Display: ${displayName}`); // <<< LOG ATUALIZADO
    setActiveTagFilter({ type, value, displayValue: displayName }); // Armazena displayValue
    // Resetar filtros da UI para evitar conflito, exceto se for o mesmo tipo/valor?
    // Por enquanto, apenas seta o filtro ativo e mostra a UI.
    // A lógica abaixo que setava gameFiltersForUI foi removida pois o filtro ativo já é suficiente
    /*
    setGameFiltersForUI(prev => ({ 
        ...prev, 
        categories: type === 'category' ? [value] : [],
        mechanics: type === 'mechanic' ? [value] : [],
        themes: type === 'theme' ? [value] : [],
    }));
    */
    setShowFilterUI(false); // Esconde filtros avançados da UI ao clicar numa tag direta
  }, []);

  // Handler para limpar filtros de TAG (usado pelo GameFilters e botão Limpar)
  const clearActiveFilter = useCallback(() => {
    setActiveTagFilter(null);
    setGameFiltersForUI({}); 
  }, []);
  
  // Handler para limpar filtro de nome de jogo
  const clearGameNameFilter = useCallback(() => {
      setGameNameFilter('');
  }, []);

  // <<< Memoizar o objeto de filtros >>>
  const feedFilters = useMemo(() => ({
    tagType: activeTagFilter?.type,
    tagValue: activeTagFilter?.value,
    filterGameName: effectiveGameNameFilter,
    sortBy: sortOption
  }), [activeTagFilter, effectiveGameNameFilter, sortOption]);
  console.log('[FeedPage] feedFilters recalculado:', feedFilters);
  
  // A key do FeedComponent deve depender do filtro efetivo
  const feedKey = JSON.stringify(feedFilters); // Usar o objeto memoizado para gerar a key

  // Log antes de renderizar
  console.log('[FeedPage DEBUG] Immediate game name filter:', gameNameFilter);
  console.log('[FeedPage DEBUG] Generated Feed Key:', feedKey);

  return (
    <>
      <SEO 
        title="Feed de Atividades"
        description="Veja as últimas atividades e posts da comunidade Boardverso."
      />
      <div className="container mx-auto max-w-4xl py-6 px-4 overflow-x-hidden">
        <h1 className="text-2xl md:text-3xl font-bold mb-6 text-gray-800 dark:text-gray-100">Feed de Atividades</h1>

        {/* Controles de Filtro e Ordenação em um card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Filtro por Nome de Jogo (controla gameNameFilter diretamente) */}
            <div className="relative flex-grow">
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-gray-500" />
                <input
                    type="text"
                    placeholder="Filtrar por jogo marcado..."
                    value={gameNameFilter}
                    onChange={(e) => setGameNameFilter(e.target.value)}
                    className="w-full pl-10 pr-8 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-indigo-500 dark:focus:border-indigo-400 sm:text-sm shadow-sm"
                />
                {gameNameFilter && (
                    <button onClick={clearGameNameFilter} className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400">
                        <X size={16} />
                    </button>
                )}
            </div>
            
            {/* Botão Filtros Avançados (Tags) */}
            <button 
                onClick={() => setShowFilterUI(!showFilterUI)}
                className="flex-shrink-0 flex items-center justify-center px-3 py-2 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 shadow-sm transition-colors"
            >
                <Filter className="h-4 w-4 mr-1" />
                {showFilterUI ? 'Esconder' : 'Mostrar'} Filtros Avançados
            </button>

            {/* Seletor de Ordenação */}
            <div className="relative flex-shrink-0">
                <select 
                    value={sortOption}
                    onChange={(e) => setSortOption(e.target.value as FeedSortOption)}
                    className="appearance-none w-full sm:w-auto pl-3 pr-8 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-indigo-500 dark:focus:border-indigo-400 sm:text-sm shadow-sm"
                >
                    <option value="created_at_desc">Mais Recentes</option>
                    <option value="created_at_asc">Mais Antigos</option>
                    <option value="like_count_desc">Mais Curtidos</option>
                </select>
                <ArrowDownUp className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500 pointer-events-none" />
            </div>
          </div>

          {/* GameFilters Condicional */}
          {showFilterUI && (
              <div className="mt-6 pt-4 border-t border-gray-100 dark:border-gray-700 overflow-x-auto max-w-full">
                  <GameFilters 
                      filters={gameFiltersForUI} 
                      showFilters={true} 
                      sortBy={'name'} 
                      setShowFilters={setShowFilterUI} 
                      onFilterChange={handleGameFilterChange} 
                      onSortChange={() => {}} 
                      onClearFilters={clearActiveFilter} 
                      hidePlayerFilter={true}
                      hideTimeFilter={true}
                      hideComplexityFilter={true}
                      hideSortSelector={true}
                  />
              </div>
          )}
        </div>

        {/* Indicador de Filtro Ativo (Tag) - Atualizado para usar displayValue */}
        {activeTagFilter && (
            <div className="mb-4 p-4 bg-indigo-50 dark:bg-indigo-900/80 border border-indigo-200 dark:border-indigo-700 rounded-lg text-sm text-indigo-800 dark:text-indigo-200 flex flex-wrap justify-between items-center gap-2">
              <div className="flex items-center">
                <span className="font-medium mr-2">Filtrando por:</span>
                <span className="px-3 py-1 bg-indigo-100 dark:bg-indigo-800 rounded-md font-bold">
                  {activeTagFilter.type === 'game' ? 'Jogo' : activeTagFilter.type.charAt(0).toUpperCase() + activeTagFilter.type.slice(1)}:
                  {' '}{activeTagFilter.displayValue || activeTagFilter.value} {/* <<< USA DISPLAY VALUE */}
                </span>
              </div>
              <button 
                onClick={clearActiveFilter} 
                className="flex items-center text-indigo-600 dark:text-indigo-300 hover:text-indigo-800 dark:hover:text-indigo-200 hover:bg-indigo-100 dark:hover:bg-indigo-700 px-3 py-1.5 rounded-md transition-colors font-medium"
              >
                <X size={16} className="mr-1"/> Limpar Filtro
              </button>
            </div>
        )}

        {/* Indicador de Filtro por Nome de Jogo */}
        {effectiveGameNameFilter && (
            <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/80 border border-blue-200 dark:border-blue-700 rounded-lg text-sm text-blue-800 dark:text-blue-200 flex flex-wrap justify-between items-center gap-2">
              <div className="flex items-center">
                <span className="font-medium mr-2">Buscando por jogo:</span>
                <span className="px-3 py-1 bg-blue-100 dark:bg-blue-800 rounded-md font-bold">
                  "{effectiveGameNameFilter}"
                </span>
              </div>
              <button 
                onClick={clearGameNameFilter} 
                className="flex items-center text-blue-600 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-200 hover:bg-blue-100 dark:hover:bg-blue-700 px-3 py-1.5 rounded-md transition-colors font-medium"
              >
                <X size={16} className="mr-1"/> Limpar Busca
              </button>
            </div>
        )}

        {/* Renderiza o FeedComponent, passando os filtros combinados */} 
        <div className=" rounded-xl pb-0">
          <FeedComponent 
              key={feedKey}
              externalFilters={feedFilters}
              onTagClick={handleTagFilterDirect}
              limit={20}
              showCreatePostForm={true}
              enableInfiniteScroll={true}
          />
        </div>
      </div>
    </>
  );
};

export default FeedPage; 